import React, { useState, useEffect, useContext, useRef } from "react";
import styles from "./BundlePage.module.css";
import axios from "axios";
import { LanguageContext } from "../../Component/LanguageContext/LanguageContext";
import { CartContext } from "../../context/CartContext";
import { Community } from "../../Component";
import { Link, useNavigate, useLocation } from "react-router-dom";
import topquote from "./topquote.png";
import bottomquote from "./bottomquote.png";
import { FiCopy, FiCheck } from "react-icons/fi";
import riyals from "./riyals.svg";
import { FaShoppingCart } from "react-icons/fa";

const BundlePage = () => {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { direction } = useContext(LanguageContext);
  const isRTL = direction === "rtl";
  const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);
  const initialRender = useRef(true);
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const navigate = useNavigate();
  const [notification, setNotification] = useState({ show: false, message: "" });

  // Scroll to top on mount and route changes
  useEffect(() => {
    if (initialRender.current) {
      window.scrollTo(0, 0);
      initialRender.current = false;
    }
  }, [location.pathname]);

  // Parse expiration date from API data
  const parseExpirationDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Check for date format like "14 March 2025 05:00 pm"
      const dateFormatRegex =
        /(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\s+(\d{1,2}):(\d{2})\s+(am|pm)/i;
      const arabicDateFormatRegex =
        /(\d{1,2})\s+(يناير|فبراير|مارس|أبريل|مايو|يونيو|يوليو|أغسطس|سبتمبر|أكتوبر|نوفمبر|ديسمبر)\s+(\d{4})\s+(\d{1,2}):(\d{2})\s+(ص|م)/i;

      // Try English format first
      const englishMatch = dateString.match(dateFormatRegex);
      if (englishMatch) {
        const [_, day, month, year, hour, minute, ampm] = englishMatch;

        // Convert month name to month number (0-11)
        const months = {
          january: 0,
          february: 1,
          march: 2,
          april: 3,
          may: 4,
          june: 5,
          july: 6,
          august: 7,
          september: 8,
          october: 9,
          november: 10,
          december: 11,
        };

        const monthIndex = months[month.toLowerCase()];
        let hours = parseInt(hour);

        // Convert 12-hour to 24-hour format
        if (ampm.toLowerCase() === "pm" && hours < 12) {
          hours += 12;
        } else if (ampm.toLowerCase() === "am" && hours === 12) {
          hours = 0;
        }

        return new Date(
          parseInt(year),
          monthIndex,
          parseInt(day),
          hours,
          parseInt(minute)
        );
      }

      // Try Arabic format
      const arabicMatch = dateString.match(arabicDateFormatRegex);
      if (arabicMatch) {
        const [_, day, month, year, hour, minute, ampm] = arabicMatch;

        // Convert Arabic month name to month number (0-11)
        const arabicMonths = {
          يناير: 0,
          فبراير: 1,
          مارس: 2,
          أبريل: 3,
          مايو: 4,
          يونيو: 5,
          يوليو: 6,
          أغسطس: 7,
          سبتمبر: 8,
          أكتوبر: 9,
          نوفمبر: 10,
          ديسمبر: 11,
        };

        const monthIndex = arabicMonths[month];
        let hours = parseInt(hour);

        // Convert Arabic 12-hour to 24-hour format
        if (ampm === "م" && hours < 12) {
          hours += 12;
        } else if (ampm === "ص" && hours === 12) {
          hours = 0;
        }

        return new Date(
          parseInt(year),
          monthIndex,
          parseInt(day),
          hours,
          parseInt(minute)
        );
      }

      // If not in the expected format, try parsing as ISO date
      let date = new Date(dateString);

      // Check if valid date
      if (isNaN(date.getTime())) {
        // If not valid ISO format, try to extract timestamp
        const timestampMatch = dateString.match(/(\d+)/);
        if (timestampMatch) {
          const timestamp = parseInt(timestampMatch[1]);
          date = new Date(timestamp * 1000); // Convert to milliseconds if it's a Unix timestamp
        } else {
          // If still not valid, return current time + 24 hours as fallback
          date = new Date();
          date.setHours(date.getHours() + 24);
        }
      }

      return date;
    } catch (err) {
      console.error("Error parsing expiration date:", err);

      // Fallback to a date 24 hours from now
      const fallbackDate = new Date();
      fallbackDate.setHours(fallbackDate.getHours() + 24);
      return fallbackDate;
    }
  };

  // Calculate time remaining until expiry date
  const calculateTimeRemaining = (expiryDate) => {
    if (!expiryDate) return { hours: 0, minutes: 0, seconds: 0 };

    const now = new Date();
    const difference = expiryDate.getTime() - now.getTime();

    if (difference <= 0) {
      return { weeks: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    // Convert to total seconds first
    const totalSeconds = Math.floor(difference / 1000);

    // Calculate weeks, days, hours, minutes, seconds
    const weeks = Math.floor(totalSeconds / (7 * 24 * 3600));
    const days = Math.floor((totalSeconds % (7 * 24 * 3600)) / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { weeks, days, hours, minutes, seconds };
  };

  useEffect(() => {
    let loadingTimeout;
    let fallbackTimeout;

    const fetchData = async () => {
      try {
        const [pageResponse, coursesResponse] = await Promise.all([
          axios.get(`${marketingBackendUrl}/maflam/bundle-page`),
          axios.get(`${marketingBackendUrl}/maflam/courses-light`),
        ]);

        setPageData(pageResponse.data.data);
        setCourses(
          Array.isArray(coursesResponse.data.courses)
            ? coursesResponse.data.courses
            : coursesResponse.data.courses || []
        );

        console.log(courses);

        // Parse expiration date from API data
        if (
          pageResponse.data.data &&
          pageResponse.data.data.component4_coupon
        ) {
          const timeString = isRTL
            ? pageResponse.data.data.component4_coupon.time_ar
            : pageResponse.data.data.component4_coupon.time_en;

          // For now, let's set a default expiration date (24 hours from now)
          // This would be replaced with actual API data when available
          const expiryDate =
            parseExpirationDate(timeString) ||
            (() => {
              const date = new Date();
              date.setHours(date.getHours() + 24);
              return date;
            })();

          setExpiryDate(expiryDate);
          setTimeRemaining(calculateTimeRemaining(expiryDate));
        }

        // Set loading false after a minimum delay
        loadingTimeout = setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();

    // Fallback timer
    fallbackTimeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => {
      clearTimeout(loadingTimeout);
      clearTimeout(fallbackTimeout);
    };
  }, [marketingBackendUrl, isRTL]);

  // Update countdown timer every second
  useEffect(() => {
    if (!expiryDate) return;

    const updateRemainingTime = () => {
      const remaining = calculateTimeRemaining(expiryDate);

      if (
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        setIsExpired(true);
      }

      setTimeRemaining(remaining);
    };

    // Initial update
    updateRemainingTime();

    // Set up interval
    const intervalId = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(intervalId);
  }, [expiryDate]);

  useEffect(() => {}, [courses]);

  // Format time with leading zeros
  const formatTime = (value) => {
    return value < 10 ? `0${value}` : value;
  };

  // Helper to convert Arabic-Indic numerals to Western numerals
  function parseArabicNumber(str) {
    if (typeof str === 'string') {
      return Number(str.replace(/[\u0660-\u0669]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
    }
    return Number(str);
  }

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <span className={styles.loader}></span>
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;
  if (!pageData || !courses) return <div>No data available</div>;

  const { title_en, title_ar, description_en, description_ar, items } =
    pageData.component1_banner;

  const coursesArray = Array.isArray(courses) ? courses : [];

  const specializedCourses = coursesArray
    .filter((course) => course?.CourseLevelEnglish === "SPECIALIZED")
    .sort(
      (a, b) =>
        Number(a?.CourseRankingEnglish || 0) -
        Number(b?.CourseRankingEnglish || 0)
    );

  const exclusiveCourses = coursesArray
    .filter((course) => course?.CourseLevelEnglish === "EXCLUSIVE")
    .sort(
      (a, b) =>
        Number(a?.CourseRankingEnglish || 0) -
        Number(b?.CourseRankingEnglish || 0)
    );

  // Find the course with rank 0
  const mainCourse = coursesArray.find(
    (course) => Number(course?.CourseRankingEnglish) === 0
  );

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    });
  };

  const addToCustomCart = async (course) => {
    // Bundle logic: Only add the bundle if any of the three editing courses are added
    const editingCourseIds = [
      '67991506f6a6c8891dd9b730', // Adobe Premiere Pro
      '679913ecf6a6c8891dd9b08c', // Vegas Pro
      '679914c8f6a6c8891dd9b630'  // DaVinci Resolve
    ];
    const bundleCourseId = '679ca8ec781c87446493d5c7';

    // If adding the bundle directly, or via editing course logic, always use correct fields
    if (course._id === bundleCourseId || editingCourseIds.includes(course._id)) {
      course = {
        _id: bundleCourseId,
        CourseNameArabic: 'حزمة أساسيات صناعة الأفلام',
        CourseNameEnglish: 'Filmmaking Fundamentals Package',
        CourseCostArabic: 2200,
        CourseCostEnglish: 2200,
        ThumbnailArabic: 'https://maflamlmsnew.s3.me-central-1.amazonaws.com/newfile.png',
        ThumbnailEnglish: 'https://maflamlmsnew.s3.me-central-1.amazonaws.com/newfile.png',
        CourseDescriptionArabic: 'اشترك في باقة أساسيات صناعة الأفلام الكاملة',
        CourseDescriptionEnglish: 'Subscribe To The Full Filmmaking Fundamentals Package',
      };
    }

    try {
      const userEmail = localStorage.getItem("useremailidis");
      
      // Validate course data before making the request
      if (!course || !course._id) {
        throw new Error("Invalid course data");
      }

      // Prepare request data
      const requestData = {
        courseId: course._id,
        courseName: isRTL ? course.CourseNameArabic : course.CourseNameEnglish,
        courseNameArabic: course.CourseNameArabic,
        courseNameEnglish: course.CourseNameEnglish,
        price: isRTL ? parseArabicNumber(course.CourseCostArabic || 0) : Number(course.CourseCostEnglish || 0),
        priceArabic: parseArabicNumber(course.CourseCostArabic || 0),
        priceEnglish: Number(course.CourseCostEnglish || 0),
        thumbnail: isRTL ? course.ThumbnailArabic : course.ThumbnailEnglish,
        description: isRTL ? course.CourseDescriptionArabic : course.CourseDescriptionEnglish,
        descriptionArabic: course.CourseDescriptionArabic,
        descriptionEnglish: course.CourseDescriptionEnglish
      };

      // Validate request data
      if (!requestData.courseId || !requestData.courseName) {
        throw new Error("Missing required course information");
      }

      let response;
      
      if (userEmail) {
        // Logged-in user: use regular cart
        const loggedInRequestData = {
          userEmail,
          ...requestData
        };
        
        response = await axios.post(
          `${marketingBackendUrl}/maflam/cart/add`,
          loggedInRequestData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 5000
          }
        );
      } else {
        // Guest user: use guest cart
        let cartId = localStorage.getItem('guestCartId');
        
        if (!cartId) {
          // Create guest cart
          const createCartResponse = await axios.post(`${marketingBackendUrl}/maflam/cart/guest`);
          cartId = createCartResponse.data.cartId;
          localStorage.setItem('guestCartId', cartId);
        }
        
        const guestRequestData = {
          cartId,
          ...requestData
        };
        
        response = await axios.post(
          `${marketingBackendUrl}/maflam/cart/guest/add`,
          guestRequestData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 5000
          }
        );
      }

      if (response.data.success) {
        // Check if the response indicates course is already in cart
        if (response.data.message && response.data.message.includes("already in cart")) {
          setNotification({
            show: true,
            message: isRTL 
              ? "الدورة موجودة بالفعل في سلة التسوق"
              : "Course Already in Cart"
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
          }, 2000);
        } else {
          setNotification({
            show: true,
            message: isRTL 
              ? "تمت إضافة الدورة إلى سلة التسوق بنجاح"
              : "Course added to cart successfully"
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
            navigate("/home/cart");
          }, 2000);
        }
      } else {
        throw new Error(response.data.message || "Failed to add to cart");
      }

    } catch (error) {
      console.error("Error adding to cart:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${marketingBackendUrl}/maflam/cart/add`
      });

      let errorMessage = isRTL
        ? "فشل في إضافة الدورة إلى سلة التسوق"
        : "Failed to add course to cart";

      if (error.response?.status === 404) {
        errorMessage += isRTL
          ? " - الخدمة غير متوفرة حالياً"
          : " - Service temporarily unavailable";
      } else if (error.response?.status === 400) {
        errorMessage += isRTL
          ? " - بيانات غير صالحة"
          : " - Invalid data";
      } else {
        errorMessage += `: ${error.response?.data?.message || error.message}`;
      }

      setNotification({
        show: true,
        message: errorMessage
      });
      setTimeout(() => {
        setNotification({ show: false, message: "" });
      }, 2000);
    }
  };

  const renderCourseCard = (course) => {
    const title = isRTL ? course.CourseNameArabic : course.CourseNameEnglish;
    const description = isRTL
      ? course.CourseDescriptionArabic
      : course.CourseDescriptionEnglish;
    const thumbnail = isRTL ? course.ThumbnailArabic : course.ThumbnailEnglish;
    const instructorImage = isRTL
      ? course.CourseInstructorPhotoArabic
      : course.CourseInstructorPhotoEnglish;
    const instructorName = isRTL
      ? course.CourseInstructorNameArabic
      : course.CourseInstructorNameEnglish;

    const maxLength = window.innerWidth <= 480 ? 150 : 260;
    const truncatedDescription =
      description?.length > maxLength
        ? `${description.substring(0, maxLength)}...`
        : description;

    const isInstructorDataAvailable =
      instructorName?.toLowerCase() !== "na" &&
      instructorImage?.toLowerCase() !== "na";

    return (
      <Link
        style={{ textDecoration: "none", color: "white" }}
        to={`/home/R2/R4/filmproduction/${course._id}`}
        className={styles.courseLink}
        key={course._id}
      >
        <div className={styles.courseCard}>
          <div className={styles.courseImage}>
            <img src={thumbnail} alt={title} />
          </div>

          <div className={styles.courseContent}>
            {/* Instructor Details */}
            {isInstructorDataAvailable && (
              <div
                style={{ marginBottom: "0px" }}
                className={styles.instructorInfo}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <img
                    src={instructorImage || "/defaultInstructor.jpg"}
                    alt={instructorName || "Instructor"}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      marginRight: "10px",
                    }}
                  />
                  <p style={{ width: "auto" }}>
                    {instructorName || "Unknown Instructor"}
                  </p>
                </div>
              </div>
            )}
            <h3 className={styles.courseTitle}>{title}</h3>
            <p className={styles.courseDescription}>{truncatedDescription}</p>
          </div>
        </div>
      </Link>
    );
  };

  const renderFeedbackSection = () => {
    const { description_en, description_ar, imageUrl } =
      pageData.component3_feedback;

    return (
      <section className={styles.feedbackSection}>
        <div className={styles.feedbackContainer}>
          <div className={styles.feedbackContent}>
            <img
              src={topquote}
              alt="Top Quote"
              className={`${styles.topQuote} ${!isRTL ? styles.ltr : ""}`}
            />
            <p className={styles.feedbackText}>
              {isRTL ? description_ar : description_en}
            </p>
            <img
              src={bottomquote}
              alt="Bottom Quote"
              className={`${styles.bottomQuote} ${!isRTL ? styles.ltr : ""}`}
            />
          </div>
          <div className={styles.feedbackImage}>
            <video
              src={imageUrl}
              alt="Student Feedback"
              controls
              loop
              muted
              preload="metadata"
              width="600"
              height="400"
            />
          </div>
        </div>
      </section>
    );
  };

  const renderCouponSection = () => {
    const {
      backgroundImageUrl,
      title_en,
      title_ar,
      description_en,
      description_ar,
      coupon_text_en,
      coupon_text_ar,
      subtitle_en,
      subtitle_ar,
      time_en,
      time_ar,
      heading_below_coupon_en,
      heading_below_coupon_ar,
    } = pageData.component4_coupon;

    const couponCode = isRTL ? coupon_text_ar : coupon_text_en;

    // Format countdown based on time remaining
    const formatCountdown = () => {
      if (isExpired) {
        return isRTL ? "انتهت الصلاحية" : "Expired";
      }

      // English format
      if (!isRTL) {
        let result = "";

        if (timeRemaining.weeks > 0) {
          result += `${timeRemaining.weeks}w `;
        }

        if (timeRemaining.days > 0 || timeRemaining.weeks > 0) {
          result += `${timeRemaining.days}d `;
        }

        result += `${formatTime(timeRemaining.hours)}h ${formatTime(
          timeRemaining.minutes
        )}m ${formatTime(timeRemaining.seconds)}s`;

        return result;
      }

      // Arabic format
      else {
        let result = "";

        if (timeRemaining.weeks > 0) {
          result += `${timeRemaining.weeks} أسبوع `;
        }

        if (timeRemaining.days > 0 || timeRemaining.weeks > 0) {
          result += `${timeRemaining.days} يوم `;
        }

        result += `${formatTime(timeRemaining.hours)} ساعة ${formatTime(
          timeRemaining.minutes
        )} دقيقة ${formatTime(timeRemaining.seconds)} ثانية`;

        return result;
      }
    };

    // Get formatted countdown text
    const countdownText = formatCountdown();

    return (
      <section className={styles.couponSection}>
        <div className={styles.couponContainer}>
          <div className={styles.couponContent}>
            <h2
              style={{ fontWeight: "400", fontSize: "24px" }}
              className={styles.couponTitle}
            >
              {isRTL ? title_ar : title_en}
            </h2>
            <h1
              style={{ fontWeight: "700", fontSize: "32px" }}
              className={styles.couponDescription}
            >
              {isRTL ? description_ar : description_en}
            </h1>
            <div
              className={`${styles.couponCode} ${copied ? styles.copied : ""}`}
              onClick={() => handleCopyCode(couponCode)}
            >
              <span>
                {isRTL ? "الكود: " : "Code: "}
                {couponCode}
              </span>
              {copied ? (
                <FiCheck className={`${styles.copyIcon} ${styles.checkIcon}`} />
              ) : (
                <FiCopy className={styles.copyIcon} />
              )}
            </div>
            <h5
              style={{
                color: "white",
                fontSize: "16px",
                fontWeight: "400",
                marginTop: "10px",
              }}
            >
              {isRTL ? subtitle_ar : subtitle_en}
            </h5>
            <h4
              style={{
                color: isExpired ? "#FF6B6B" : "white",
                fontSize: "24px",
                fontWeight: "400",
                marginTop: "10px",
              }}
              className={styles.couponTime}
            >
              {isExpired ? (
                <span style={{ color: "#FF6B6B" }}>
                  {isRTL ? "انتهت الصلاحية" : "Expired"}
                </span>
              ) : (
                <span>
                  {isRTL ? "ينتهي الكوبون خلال: " : "Coupon Expires in: "}
                  <span style={{ color: "rgb(57,255,251)" }}>
                    {countdownText}
                  </span>
                </span>
              )}
            </h4>
          </div>
        </div>
        <h1
          style={{
            color: "white",
            fontSize: "36px",
            fontWeight: "700",
            marginTop: "10px",
            width: "100%",
            // marginTop: "200px",
            textAlign: "center",
          }}
        >
          {isRTL ? heading_below_coupon_ar : heading_below_coupon_en}
        </h1>
      </section>
    );
  };

  const renderBigCTASection = () => {
    const {
      title_en,
      title_ar,
      cuttedPrice_en,
      cuttedPrice_ar,
      price_en,
      price_ar,
      buttonText_en,
      buttonText_ar,
      pointers_en,
      pointers_ar,
    } = pageData.component5_bigCTA;

    return (
      <section className={styles.bigCTASection}>
        <div className={styles.bigCTAContainer}>
          <div className={styles.bigCTALeft}>
            <h2 className={styles.bigCTATitle}>
              {isRTL ? title_ar : title_en}
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                textAlign: "center",
              }}
              className={styles.bigCTAPriceContainer}
            >
              <span className={styles.bigCTAActualPrice}>
                {isRTL ? cuttedPrice_ar : cuttedPrice_en}

                <img
                  className={styles.bigCTARiyalsIcon}
                  src={riyals}
                  alt="riyals"
                />
              </span>
            </div>
          </div>

          <div className={styles.bigCTARight}>
            <div className={styles.pointersContainer}>
              {(isRTL ? pointers_ar : pointers_en).map((pointer, index) => (
                <div key={index} className={styles.pointer}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 0C5.373 0 0 5.373 0 12C0 18.627 5.373 24 12 24C18.627 24 24 18.627 24 12C24 5.373 18.627 0 12 0ZM10.75 17.292L6.25 12.928L8.107 11.07L10.75 13.576L16.393 7.792L18.25 9.649L10.75 17.292Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>{pointer}</span>
                </div>
              ))}
            </div>
            <div className={styles.buttonContainer}>
              <Link
                style={{ textDecoration: "none" }}
                to={mainCourse ? `/home/checkout/${mainCourse._id}` : "#"}
                className={styles.subscribeButton}
              >
                {isRTL ? "اشترك الآن" : "Subscribe"}
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      {/* Notification Component */}
      {notification.show && (
        <div
          style={{
            position: 'fixed',
            top: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'black',
            color: 'rgb(57, 255, 251)',
            padding: '15px 30px',
            borderRadius: '4px',
            zIndex: 999999,
            textAlign: 'center',
            fontFamily: 'Noto Kufi Arabic, sans-serif',
            border: '1px solid rgba(57, 255, 251, 0.2)',
            maxWidth: '400px',
            wordWrap: 'break-word'
          }}
        >
          {notification.message}
        </div>
      )}
      
      <div className={styles.bundlePage}>
        <div className={styles.container}>
          <section className={styles.heroSection}>
            <div className={styles.header}>
              <h1 className={styles.title}>{isRTL ? title_ar : title_en}</h1>
              <p className={styles.description}>
                {isRTL ? description_ar : description_en}
              </p>
              <div className={styles.heroFooter}>
                <div className={styles.heroStats}>
                  <div className={styles.price}>
                    <span style={{ color: "rgb(57, 255, 251)" }}>
                      <svg
                        style={{ marginLeft: "5px", marginRight: "5px" }}
                        width="26"
                        height="15"
                        viewBox="0 0 26 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M24.4316 7.14843C23.8954 7.14843 23.4614 6.73004 23.4614 6.21398C23.4614 6.20102 23.4647 6.18912 23.4651 6.17651H2.53436C2.53472 6.18912 2.53836 6.20102 2.53836 6.21398C2.53836 6.73004 2.10398 7.14843 1.56783 7.14843C1.55474 7.14843 1.54275 7.14527 1.5293 7.14492V14.0316C1.54275 14.0313 1.55474 14.0281 1.56783 14.0281C2.10398 14.0281 2.53836 14.4469 2.53836 14.9626C2.53836 14.9755 2.53472 14.9874 2.53436 15H23.4651C23.4647 14.9874 23.4614 14.9755 23.4614 14.9626C23.4614 14.4469 23.8954 14.0281 24.4316 14.0281C24.445 14.0281 24.4574 14.0313 24.4705 14.0316V7.14492C24.4574 7.14527 24.445 7.14843 24.4316 7.14843ZM12.9997 14.1402C11.5312 14.1402 10.3407 12.55 10.3407 10.5879C10.3407 8.6259 11.5312 7.03639 12.9997 7.03639C14.4682 7.03639 15.6587 8.6266 15.6587 10.5879C15.6587 12.5493 14.4682 14.1402 12.9997 14.1402Z"
                          fill="#5AFFFF"
                        />
                        <path
                          d="M23.7211 0.746413C23.7078 0.749497 23.6963 0.755323 23.6831 0.758065C23.144 0.874928 22.6083 0.552442 22.4862 0.038383C22.4833 0.0253602 22.4837 0.0130228 22.4808 0L1.42837 4.55936C1.4316 4.57169 1.43842 4.58266 1.44165 4.59534C1.56375 5.10974 1.2262 5.6214 0.686832 5.73826C0.673187 5.74101 0.660618 5.74066 0.646973 5.7434L1.16946 7.94118V6.09159C1.18346 6.09194 1.19567 6.09502 1.20932 6.09502C1.76233 6.09502 2.21012 5.66732 2.21012 5.14024C2.21012 5.12688 2.20653 5.11488 2.20617 5.10186H10.3307C10.6008 4.09362 11.2572 3.33178 12.1578 3.13679C13.3673 2.87496 14.6413 3.71562 15.3186 5.10186H23.7929C23.7926 5.11488 23.7893 5.12688 23.7893 5.14024C23.7893 5.66767 24.2371 6.09502 24.7898 6.09502C24.8038 6.09502 24.8164 6.09194 24.83 6.09159V7.86681C24.9586 7.74549 25.123 7.6557 25.3126 7.61458C25.3266 7.61184 25.3396 7.61218 25.3529 7.60944L23.7211 0.746413Z"
                          fill="#5AFFFF"
                        />
                      </svg>

                      {isRTL
                        ? mainCourse?.CourseCostArabic
                        : mainCourse?.CourseCostEnglish}
                      <img
                        style={{
                          width: "20px",
                          height: "20px",
                          marginLeft: "5px",
                          marginRight: "5px",
                        }}
                        src={riyals}
                        alt="riyals"
                      />
                    </span>
                  </div>
                  <div style={{ color: "white" }} className={styles.duration}>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.5 19C4.2533 19 0 14.7465 0 9.5C0 4.25348 4.2533 0 9.5 0C14.7467 0 19 4.25348 19 9.5C19 14.7465 14.7465 19 9.5 19ZM13.258 8.34136L8.65981 5.10507C7.72107 4.4444 6.42759 5.11589 6.42759 6.26428V12.7355C6.42759 13.8837 7.72107 14.5554 8.65981 13.8947L13.258 10.6584C14.0599 10.094 14.0599 8.9058 13.258 8.34136Z"
                        fill="#5AFFFF"
                      />
                    </svg>

                    <span>
                      {isRTL
                        ? mainCourse?.LectureCountArabic
                        : mainCourse?.LectureCountEnglish}
                    </span>
                  </div>
                  <div style={{ color: "white" }} className={styles.duration}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17316C0.00433284 8.00043 -0.1937 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8078C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17316C18.7362 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87103 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99112 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34314C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21446 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z"
                        fill="#39FFFB"
                      />
                      <path
                        d="M13.9998 9H10.9998V6C10.9998 5.73478 10.8944 5.48043 10.7069 5.29289C10.5193 5.10536 10.265 5 9.99976 5C9.73454 5 9.48019 5.10536 9.29265 5.29289C9.10511 5.48043 8.99976 5.73478 8.99976 6V10C8.99976 10.2652 9.10511 10.5196 9.29265 10.7071C9.48019 10.8946 9.73454 11 9.99976 11H13.9998C14.265 11 14.5193 10.8946 14.7069 10.7071C14.8944 10.5196 14.9998 10.2652 14.9998 10C14.9998 9.73478 14.8944 9.48043 14.7069 9.29289C14.5193 9.10536 14.265 9 13.9998 9Z"
                        fill="#39FFFB"
                      />
                    </svg>
                    <span>
                      {isRTL
                        ? mainCourse?.EstimatedLearningTimeArabic
                        : mainCourse?.EstimatedLearningTimeEnglish}
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Link
                    style={{ textDecoration: "none" }}
                    to={mainCourse ? `/home/checkout/${mainCourse._id}` : "#"}
                    className={styles.subscribeButton}
                  >
                    {isRTL ? "اشترك الآن" : "Subscribe"}
                  </Link>

                  <button
                    className={styles.subscribeButton}
                    onClick={() => addToCustomCart(mainCourse)}
                    style={{
                      backgroundColor: "rgb(57,255,251)",
                      color: "black",
                      border: "none",
                      padding: "12px 20px",
                      fontSize: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "none"
                    }}
                  >
                    <FaShoppingCart style={{ height: "24px", width: "24px", marginRight: "10px" }} />
                    <span>
                      {isRTL ? "إضافة إلى سلة التسوق" : "Add to Cart"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.featuresGrid}>
              <div className={styles.topRow}>
                {items.slice(0, 3).map((item, index) => (
                  <div key={index} className={styles.featureCard}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={item.imageUrl}
                        alt={isRTL ? item.description_ar : item.description_en}
                        className={styles.icon}
                      />
                    </div>
                    <h3 className={styles.featureTitle}>
                      {isRTL ? item.description_ar : item.description_en}
                    </h3>
                    {index === 2 && <p className={styles.subText}></p>}
                  </div>
                ))}
              </div>
              <div className={styles.bottomRow}>
                {items.slice(3).map((item, index) => (
                  <div key={index + 3} className={styles.featureCard}>
                    <div className={styles.iconWrapper}>
                      <img
                        src={item.imageUrl}
                        alt={isRTL ? item.description_ar : item.description_en}
                        className={styles.icon}
                      />
                    </div>
                    <h3 className={styles.featureTitle}>
                      {isRTL ? item.description_ar : item.description_en}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={styles.smallCTASection}>
            <div className={styles.smallCTAContainer}>
              <h2 className={styles.smallCTATitle}>
                {isRTL
                  ? pageData.component2_smallCTA.title_ar
                  : pageData.component2_smallCTA.title_en}
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
                className={styles.priceContainer}
              >
                <span
                  className={styles.cuttedPrice}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {isRTL
                      ? pageData.component2_smallCTA.cuttedPrice_ar
                      : pageData.component2_smallCTA.cuttedPrice_en}
                  </span>
                  <span>
                    <img
                      style={{
                        height: "24px",
                        position: "relative",
                        top: "5px",
                        marginLeft: "10px",
                        marginRight: "10px",
                        filter:
                          "brightness(0) saturate(100%) invert(83%) sepia(83%) saturate(1033%) hue-rotate(113deg) brightness(103%) contrast(103%)",
                      }}
                      src={riyals}
                      alt="riyals"
                    />
                  </span>
                </span>
                <span
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <span className={styles.actualPrice}>
                    {isRTL
                      ? pageData.component2_smallCTA.price_ar
                      : pageData.component2_smallCTA.price_en}
                  </span>
                  <span>
                    <img
                      style={{
                        height: "40px",
                        marginLeft: "15px",
                        marginRight: "15px",
                        position: "relative",
                        top: "5px",
                      }}
                      src={riyals}
                      alt="riyals"
                    />
                  </span>
                </span>
              </div>
              <Link
                to={mainCourse ? `/home/checkout/${mainCourse._id}` : "#"}
                className={styles.ctaButton}
              >
                {isRTL ? "اشترك الآن" : "Subscribe"}
              </Link>
            </div>
          </section>

          {renderFeedbackSection()}
          {renderCouponSection()}

          <section className={styles.coursesSection}>
            <h2 className={styles.sectionTitle}>
              {isRTL ? "الدورات المتخصصة" : "Specialized Courses"}
            </h2>
            <div className={styles.coursesGrid}>
              {specializedCourses.map(renderCourseCard)}
            </div>
          </section>

          <section className={styles.coursesSection}>
            <h2 className={styles.sectionTitle}>
              {isRTL ? "الدورات الحصرية" : "Exclusive Courses"}
            </h2>
            <div className={styles.coursesGrid}>
              {exclusiveCourses.map(renderCourseCard)}
            </div>
          </section>
          {renderBigCTASection()}
        </div>
      </div>
      <Community />
    </>
  );
};

export default BundlePage;
